import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { Member } from "@/types/member";
import styles from "./reminder.module.css";

export interface AddReminderMemberDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelectMember: (member: Member) => void;
}

const getInitials = (firstName?: string, lastName?: string): string =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

/**
 * "Who is this reminder for?" step shown before reminder-type selection —
 * mirrors MProfilePicker's member list (same managed-members source, same
 * "You" / registration-status treatment) but as a one-time picker in the
 * add-reminder flow rather than a persistent switcher.
 */
const AddReminderMemberDrawer: React.FC<AddReminderMemberDrawerProps> = ({
  open,
  onClose,
  onSelectMember,
}) => {
  const { t } = useTranslation();
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();
  const loggedInMember = useSelector((state: RootState) => state.auth.member);

  useEffect(() => {
    if (open && managedMembers.length === 0) {
      fetchManagedMembers();
    }
  }, [open, managedMembers.length, fetchManagedMembers]);

  const isYou = (member: Member) => member.id === loggedInMember?.id;

  const sortedMembers = [...managedMembers].sort((a, b) => {
    if (isYou(a)) return -1;
    if (isYou(b)) return 1;
    return (a.first_name || "").localeCompare(b.first_name || "");
  });

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title={t("reminders.addReminder.drawerTitle")}
      // Capped like the reminders listing's member filter drawer — the
      // default "auto" height grows to fit every family member with no
      // limit, opening almost full-screen for a large family.
      height="55vh"
    >
      <div className={styles.memberDrawerIntro}>
        <p className={styles.memberDrawerCaption}>
          {t("reminders.addReminder.notificationsToYou")}
        </p>
      </div>
      <div className={styles.memberDrawerList}>
        {sortedMembers.map((member) => {
          const registered =
            isYou(member) || member.registration_status === "accepted";
          return (
            <button
              key={member.id}
              type="button"
              className={styles.memberDrawerRow}
              onClick={() => onSelectMember(member)}
            >
              <div
                className={
                  registered
                    ? styles.memberDrawerAvatar
                    : styles.memberDrawerAvatarOutline
                }
              >
                {member.avatar ? (
                  <img
                    src={member.avatar}
                    alt=""
                    className={styles.memberDrawerAvatarImg}
                  />
                ) : (
                  <span>
                    {getInitials(member.first_name, member.last_name)}
                  </span>
                )}
              </div>
              <span className={styles.memberDrawerName}>
                {isYou(member)
                  ? t("reminders.addReminder.you")
                  : member.nickname || member.first_name}
              </span>
            </button>
          );
        })}
      </div>
    </FooterDrawer>
  );
};

export default AddReminderMemberDrawer;
